import { ImFacebook2, ImInstagram, ImTwitter } from "react-icons/im";
import { FaWhatsapp } from "react-icons/fa";
import { GlobalData } from "../../assets/data/GlodalData";
import { MdArrowOutward } from "react-icons/md";
import { useState, useEffect, useCallback } from "react";
import GoogleTranslateButton from "../Tools/GoogleTranslator";

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [isScrolling, setIsScrolling] = useState(false);
  const [showScrollDown, setShowScrollDown] = useState(true);

  // Debounce function to detect when scrolling stops
  const debounce = (func, wait) => {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  };

  // Handle scroll events
  const handleScroll = useCallback(() => {
    setIsScrolling(true);
    setShowScrollDown(false);

    // If user is near bottom, hide the scroll down indicator
    if (
      window.innerHeight + window.scrollY >=
      document.body.offsetHeight - 100
    ) {
      setShowScrollDown(false);
    }
  }, []);

  // Debounced version of handleScroll
  const debouncedHandleScroll = useCallback(
    debounce(() => {
      setIsScrolling(false);

      // Only show scroll down if we're not at the bottom
      if (
        window.innerHeight + window.scrollY <
        document.body.offsetHeight - 100
      ) {
        setShowScrollDown(true);
      }
    }, 150),
    []
  );

  // Combined scroll handler
  const combinedScrollHandler = useCallback(() => {
    handleScroll();
    debouncedHandleScroll();
  }, [handleScroll, debouncedHandleScroll]);

  useEffect(() => {
    // Check initial position
    if (
      window.innerHeight + window.scrollY >=
      document.body.offsetHeight - 100
    ) {
      setShowScrollDown(false);
    }

    window.addEventListener("scroll", combinedScrollHandler, { passive: true });

    return () => {
      window.removeEventListener("scroll", combinedScrollHandler);
    };
  }, [combinedScrollHandler]);

  // Check if user is at the bottom on mount
  useEffect(() => {
    const checkInitialPosition = () => {
      if (
        window.innerHeight + window.scrollY >=
        document.body.offsetHeight - 100
      ) {
        setShowScrollDown(false);
      }
    };

    // Small delay to ensure DOM is fully loaded
    setTimeout(checkInitialPosition, 100);
  }, []);

  return (
    <main className="bg-primary justify-center max-w-7xl mx-auto px-4 md:px-8 my-6 rounded-3xl">
      {/* Footer */}
      <section className="">
        <footer>
          <div className="max-w-screen-xl px-4 py-16 mx-auto sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 gap-8 lg:grid-cols-4">
              {/* Brand Section */}
              <div className="lg:col-span-2">
                <div className="mb-6">
                  <h2 className="text-3xl font-bold text-white">
                    DERMA Slim & Wellness
                  </h2>
                  <p className="text-lg text-white mt-2">
                    Personalised solutions for Skin, Hair & Body
                  </p>
                  <a
                    href="/contactus"
                    className="inline-flex items-center gap-2 bg-white text-gray-900 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
                  >
                    Book Appointment
                    <MdArrowOutward className="text-md" />
                  </a>
                </div>
              </div>

              {/* Contact Information */}
              <div>
                <p className="font-semibold text-white text-lg mb-4">
                  Contact Us
                </p>
                <div className="space-y-3 text-white">
                  <p className="text-sm">
                    For more information
                    <br />
                    <span className="font-medium text-white">
                      {GlobalData.company.companyPhone}
                    </span>
                  </p>
                  <p className="text-sm">
                    Email
                    <br />
                    <span className="font-medium text-white">
                      {GlobalData.company.companyEmail}
                    </span>
                  </p>
                </div>
              </div>

              {/* Quick Links */}
              <div>
                <p className="font-semibold text-white text-lg mb-4">
                  Quick Link
                </p>
                <nav className="flex flex-col space-y-2 text-white">
                  <a href="/" className="hover:text-white transition-colors">
                    Home
                  </a>
                  <a
                    href="/about"
                    className="hover:text-white transition-colors"
                  >
                    About Us
                  </a>
                  <a
                    href="/blog"
                    className="hover:text-white transition-colors"
                  >
                    Blog
                  </a>
                  <a
                    href="/contact"
                    className="hover:text-white transition-colors"
                  >
                    Contact
                  </a>
                </nav>
              </div>
            </div>

            {/* Get In Touch Section */}
            <div className="mt-12 pt-8 border-t border-white">
              <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center">
                <div className="mb-6 lg:mb-0">
                  <p className="font-semibold text-white mb-2">Get In Touch</p>
                  <p className="text-sm text-white max-w-md">
                    {GlobalData.company.companyAddress}
                  </p>
                </div>

                {/* Social Media Links */}
                <span className="text-white">Follow Us On Social</span>
                <div className="flex space-x-4">
                  <a
                    href="#"
                    className="text-white hover:text-white transition-colors"
                  >
                    <ImFacebook2 size={20} />
                  </a>
                  <a
                    href="#"
                    className="text-white hover:text-white transition-colors"
                  >
                    <ImInstagram size={20} />
                  </a>
                  <a
                    href="#"
                    className="text-white hover:text-white transition-colors"
                  >
                    <ImTwitter size={20} />
                  </a>
                </div>
              </div>
            </div>

            {/* Bottom Section */}
            <div className="mt-8 pt-8 border-t border-white relative z-10"> {/* Added relative z-10 */}
              <div className="flex flex-col lg:flex-row justify-between items-center space-y-4 lg:space-y-0">
                <div className="text-center lg:text-left">
                  <p className="text-sm text-white">
                    Copyright © {currentYear} All Rights Reserved.
                  </p>
                  <p className="text-xs text-white mt-1">
                    Derma slim & wellness,
                    <br />
                    <a
                      href="https://cyberspacedigital.in/contactus"
                      className="font-bold hover:underline hover:text-white transition-colors"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Developed by Cyber Space Digital.
                    </a>
                  </p>
                </div>

                <div className="flex space-x-6 text-sm text-white">
                  <a
                    href="/terms"
                    className="hover:text-white transition-colors"
                  >
                    Terms & Conditions
                  </a>
                  <a
                    href="/privacy"
                    className="hover:text-white transition-colors"
                  >
                    Privacy Policy
                  </a>
                </div>
              </div>
            </div>
          </div>
        </footer>
      </section>

      {/* WhatsApp Floating Button - Split Layout */}
      {/* Changed from fixed full-width container to individual fixed elements */}
      {/* Left Side: Scroll Down Indicator */}
      <div className="fixed left-4 z-40 flex flex-col items-start"
        style={{
          bottom: "calc(env(safe-area-inset-bottom, 0px) + 1.5rem)",
        }}
      >
        {showScrollDown && !isScrolling && (
          <div
            className="flex flex-col items-center space-y-1 transition-all duration-300"
            style={{
              opacity: showScrollDown ? 1 : 0,
              transform: `translateY(${showScrollDown ? "0" : "20px"})`,
            }}
          >
            <div className="animate-bounce">
              <div className="w-6 h-10 border-2 border-black/80 rounded-full flex justify-center">
                <div className="w-1 h-3 bg-black/80 rounded-full mt-2"></div>
              </div>
            </div>
            <span className="text-[10px] font-medium tracking-wider text-black/80">
              SCROLL DOWN
            </span>
          </div>
        )}
      </div>

      

      {/* Right Side: Language + WhatsApp Stack */}
      <div className="fixed right-4 z-40 flex flex-col items-end space-y-4"
        style={{
          bottom: "calc(env(safe-area-inset-bottom, 0px) + 1.5rem)",
        }}
      >
        {/* Language Selector */}
        <div className="flex flex-col items-center">
          <GoogleTranslateButton />
        </div>

        {/* WhatsApp Button */}
         <a
            href={`tel:${GlobalData.company.companyPhone}`}
            className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center shadow-lg hover:bg-green-700 transition-colors"
          >
            <svg
              className="w-5 h-5 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
              />
            </svg>
          </a>
          
        <a
          href="https://wa.me/918977733002"
          target="_blank"
          rel="noopener noreferrer"
          className="w-14 h-14 flex items-center justify-center bg-[#25D366] text-white rounded-full shadow-lg hover:bg-[#128C7E] transition-all duration-300 hover:scale-110 hover:shadow-xl z-50"
        >
          <FaWhatsapp size={28} />
        </a>


       
         


      </div>


      
    </main>
  );
};

export default Footer;